import React from 'react';
import { PageHeader, SectionHeader } from '../components/SectionHeader';
import { FieldDiagram } from '../components/FieldDiagram';
import { ScoringTable, ScoringCard } from '../components/ScoringTable';
import { Ruler, Target, Flag, Footprints, Award, Hand, Crosshair } from 'lucide-react';
import { scoringData } from '../data/nflData';

const Rules = () => {
  const scoringIcons = {
    "Touchdown": Award,
    "Punto Extra (PAT)": Target,
    "Conversión de 2 puntos": Crosshair,
    "Field Goal": Flag,
    "Safety": Hand
  };

  return (
    <div className="min-h-screen" data-testid="rules-page">
      <PageHeader 
        title="Cómo se Juega"
        subtitle="Las reglas básicas del fútbol americano explicadas de forma sencilla"
        backgroundImage="https://images.unsplash.com/photo-1547992454-bec8143cc790?auto=format&fit=crop&w=2000&q=80"
      />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        
        {/* Field Section */}
        <section>
          <SectionHeader 
            title="El Campo de Juego"
            quarter="1Q"
            icon={Ruler}
          />
          
          <div className="space-y-8">
            <p className="text-slate-300 leading-relaxed text-lg">
              El campo de fútbol americano es un rectángulo de <strong className="text-white">100 yardas</strong> de largo 
              (más 10 yardas de zona de anotación en cada extremo) y aproximadamente 53 yardas de ancho.
            </p>
            
            <div className="glow-green rounded-2xl overflow-hidden">
              <FieldDiagram />
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: "Líneas de Yardas",
                  description: "Marcadas cada 5 yardas, con números cada 10. Van del 1 al 50 y de vuelta al 1."
                },
                {
                  title: "Línea de Scrimmage",
                  description: "Línea imaginaria donde comienza cada jugada, marcada por la posición del balón."
                },
                {
                  title: "End Zones",
                  description: "Zonas de anotación de 10 yardas en cada extremo. Aquí se anotan los touchdowns."
                }
              ].map((item, index) => (
                <div key={index} className="tactical-card p-5 rounded-r-xl">
                  <h4 
                    className="text-lg font-bold text-white mb-2 uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {item.title}
                  </h4>
                  <p className="text-slate-400 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Scoring Section */}
        <section>
          <SectionHeader 
            title="Cómo se Anotan Puntos"
            quarter="2Q"
            icon={Target}
          />
          
          <div className="space-y-8">
            <p className="text-slate-300 leading-relaxed">
              Hay varias formas de sumar puntos en el fútbol americano. El <strong className="text-emerald-400">touchdown</strong> es 
              la anotación principal, pero hay otras formas de puntuar que pueden ser decisivas en partidos cerrados.
            </p>
            
            {/* Desktop table */}
            <div className="hidden md:block">
              <ScoringTable />
            </div>
            
            {/* Mobile cards */}
            <div className="md:hidden space-y-4">
              {scoringData.map((item, index) => (
                <ScoringCard 
                  key={index}
                  type={item.type}
                  points={item.points}
                  description={item.description}
                  icon={scoringIcons[item.type]}
                />
              ))}
            </div>
            
            <div className="bg-emerald-900/20 border border-emerald-800 p-6 rounded-xl">
              <h4 
                className="text-lg font-bold text-emerald-400 mb-3 flex items-center gap-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                <Award className="w-5 h-5" />
                Ejemplo de marcador
              </h4>
              <p className="text-slate-300">
                Si tu equipo anota un <strong className="text-white">touchdown (6)</strong> y un 
                <strong className="text-white"> punto extra (1)</strong>, sumas <strong className="text-emerald-400">7 puntos</strong>. 
                Si en el siguiente drive anotas un <strong className="text-white">field goal (3)</strong>, 
                llevas <strong className="text-emerald-400">10 puntos</strong> en total.
              </p>
            </div>
          </div>
        </section>

        {/* Downs Section */}
        <section>
          <SectionHeader 
            title="Downs y Avance del Balón"
            quarter="3Q"
            icon={Footprints}
          />
          
          <div className="space-y-8">
            <p className="text-slate-300 leading-relaxed text-lg">
              El sistema de <strong className="text-orange-400">downs</strong> es el corazón del juego. 
              El equipo ofensivo tiene <strong className="text-white">4 oportunidades</strong> para avanzar 
              al menos <strong className="text-white">10 yardas</strong>.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="info-card">
                <h4 
                  className="text-xl font-bold text-white mb-4 uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  El ciclo de downs
                </h4>
                <div className="space-y-4">
                  {[
                    { down: "1º y 10", desc: "Primera oportunidad. Necesitas 10 yardas." },
                    { down: "2º y X", desc: "Segunda oportunidad. X = yardas restantes." },
                    { down: "3º y X", desc: "Tercera oportunidad. Momento crítico." },
                    { down: "4º y X", desc: "Última oportunidad. Decisión importante." }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <span 
                        className="w-20 text-center py-2 bg-slate-700 rounded-lg text-emerald-400 font-bold text-sm"
                        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                      >
                        {item.down}
                      </span>
                      <span className="text-slate-400 text-sm">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="info-card">
                <h4 
                  className="text-xl font-bold text-white mb-4 uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  ¿Qué pasa en 4º down?
                </h4>
                <div className="space-y-4">
                  {[
                    { 
                      option: "Punt", 
                      desc: "Patear para alejar el balón del rival. La opción más común.",
                      color: "#3b82f6"
                    },
                    { 
                      option: "Field Goal", 
                      desc: "Si estás cerca (< 50 yardas), intenta 3 puntos.",
                      color: "#f97316"
                    },
                    { 
                      option: "Go for it", 
                      desc: "Arriesgarse e intentar conseguir las yardas. Alta recompensa, alto riesgo.",
                      color: "#ef4444"
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span 
                        className="px-3 py-1 text-xs font-bold rounded text-white"
                        style={{ backgroundColor: item.color, fontFamily: 'Barlow Condensed, sans-serif' }}
                      >
                        {item.option}
                      </span>
                      <span className="text-slate-400 text-sm">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
              <h4 
                className="text-lg font-bold text-white mb-3"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                ¿Por qué "primer y diez"?
              </h4>
              <p className="text-slate-400">
                Cuando consigues 10+ yardas, el marcador vuelve a "1º y 10" (first and ten). 
                Si avanzas más de 10 yardas en una sola jugada, sigues con un nuevo 1º y 10. 
                Si avanzas exactamente 10 yardas en 3 jugadas, consigues el first down en el tercer intento.
              </p>
            </div>
          </div>
        </section>

        {/* Basic plays */}
        <section>
          <SectionHeader 
            title="Formas de Jugar una Jugada"
            quarter="4Q"
            icon={Flag}
          />
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Carrera (Run)",
                description: "El quarterback entrega el balón a un corredor que intenta avanzar por tierra, buscando huecos en la defensa.",
                icon: Footprints,
                color: "#3b82f6"
              },
              {
                title: "Pase (Pass)",
                description: "El quarterback lanza el balón a un receptor que intenta atraparlo y avanzar. Más arriesgado pero puede ganar más yardas.",
                icon: Target,
                color: "#22c55e"
              },
              {
                title: "Pateo (Kick)",
                description: "Incluye punts (despejes), field goals (3 puntos) y kickoffs (saques iniciales). Especialistas dedicados.",
                icon: Flag,
                color: "#f97316"
              }
            ].map((play, index) => (
              <div key={index} className="position-card">
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: play.color }} />
                <div className="p-6">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${play.color}20` }}
                  >
                    <play.icon className="w-6 h-6" style={{ color: play.color }} />
                  </div>
                  <h3 
                    className="text-xl font-bold text-white mb-3 uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {play.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{play.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 tactical-card p-6 rounded-r-xl">
            <h4 
              className="text-xl font-bold text-white mb-4 uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              El Snap: inicio de cada jugada
            </h4>
            <p className="text-slate-400 leading-relaxed">
              Cada jugada comienza con el <strong className="text-white">snap</strong>: el centro pasa el balón al quarterback 
              (o al punter/holder en jugadas especiales). Hasta el snap, los jugadores deben permanecer quietos. 
              El quarterback dice un código ("hike", "hut", números) para indicar cuándo quiere el balón.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Rules;
